import { CheckCircle2, FlaskConical, BarChart3, FileCheck } from 'lucide-react';

const qaData = [
  {
    icon: FlaskConical,
    title: 'HPLC Analysis',
    description: 'Every batch undergoes High-Performance Liquid Chromatography (HPLC) testing by independent third-party laboratories. Chromatographic purity consistently exceeds 99%.',
  },
  {
    icon: BarChart3,
    title: 'Mass Spectrometry',
    description: 'Liquid Chromatography-Mass Spectrometry (LC-MS) confirms molecular identity and detects impurities at parts-per-billion resolution. No batch ships without MS verification.',
  },
  {
    icon: FileCheck,
    title: 'Certificate of Analysis',
    description: 'Every shipment includes a comprehensive CoA with HPLC chromatograms, MS data, appearance, solubility, and endotoxin testing results. Full traceability guaranteed.',
  },
  {
    icon: CheckCircle2,
    title: 'Batch Consistency',
    description: 'Rigorous process controls ensure lot-to-lot reproducibility. Our suppliers operate under cGMP-aligned manufacturing standards with complete audit trails.',
  },
];

export default function QualityAssurance() {
  return (
    <section id="quality" data-testid="quality-section" className="relative py-24 bg-[#F8F9FA] overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/658f6bc2-5a18-41fb-b6c0-5fd702099d25/images/75aeb231813401bae48ffeea495ab9b0f3147cc77e638a4075a07e85cb8ddf1c.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left column - header */}
          <div className="lg:col-span-4">
            <span
              className="text-xs tracking-widest uppercase text-[#0A7C84] mb-3 block"
              style={{ fontFamily: 'var(--font-mono)' }}
            >
              Quality Assurance
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A1128] mb-6"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 900 }}
              data-testid="quality-heading"
            >
              Zero-Compromise
              <br />
              Purity
            </h2>
            <p
              className="text-sm text-[#495057] leading-relaxed mb-8"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              Our quality infrastructure is designed for one outcome: absolute confidence in every milligram you receive. We partner exclusively with ISO 17025-accredited testing laboratories for independent verification.
            </p>

            {/* Compliance badges */}
            <div className="flex flex-wrap gap-3">
              {['HPLC', 'LC-MS', 'ISO 17025', 'cGMP-Aligned'].map(badge => (
                <span
                  key={badge}
                  className="px-3 py-1.5 border border-[#0A7C84]/20 text-[#0A7C84] text-xs tracking-wider"
                  style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}
                  data-testid={`quality-badge-${badge.toLowerCase().replace(/[/ ]/g, '-')}`}
                >
                  {badge}
                </span>
              ))}
            </div>
          </div>

          {/* Right column - grid */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border border-[#DEE2E6]">
              {qaData.map((item, idx) => (
                <div
                  key={item.title}
                  className={`p-8 ${idx % 2 === 0 ? 'sm:border-r border-[#DEE2E6]' : ''} ${idx < 2 ? 'border-b border-[#DEE2E6]' : ''}`}
                  data-testid={`quality-item-${idx}`}
                >
                  <item.icon className="w-5 h-5 text-[#0A7C84] mb-4" strokeWidth={1.5} />
                  <h3
                    className="text-lg tracking-tight text-[#0A1128] mb-2"
                    style={{ fontFamily: 'var(--font-heading)', fontWeight: 700 }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#495057] leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
